import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { CommunityDetail, CommunityStructure } from "@atrium/shared";
import { api, errorMessage } from "../../../lib/api";
import { toast } from "../../../components/ui/Toast";

export type Patch = Partial<{
  name: string;
  description: string;
  visibility: "public" | "private";
  iconMediaId: string | null;
  bannerMediaId: string | null;
  topics: string[];
  publishToFeed: boolean;
  showMembersPublicly: boolean;
  blockedWords: string[];
  accentColor: string | null;
  bannerColor: CommunityDetail["bannerColor"];
  showBannerInSidebar: boolean;
  defaultChannelId: string | null;
  joinAnnounceChannelId: string | null;
  rules: string;
  minAccountAgeDays: number;
  blockLinks: boolean;
  ownerId: string;
}>;

/** Refresh everything that shows community data after a change. */
export function useRefreshCommunity(communityId: string) {
  const qc = useQueryClient();
  return async () => {
    await qc.invalidateQueries({ queryKey: ["structure", communityId] });
    void qc.invalidateQueries({ queryKey: ["my-communities"] });
    void qc.invalidateQueries({ queryKey: ["community"] });
    void qc.invalidateQueries({ queryKey: ["audit", communityId] });
  };
}

export function usePatchCommunity(communityId: string) {
  const refresh = useRefreshCommunity(communityId);
  return async (patch: Patch, ok = "Saved") => {
    try {
      await api.patch(`/api/communities/${communityId}`, patch);
      await refresh();
      if (ok) toast.show(ok);
      return true;
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
  };
}

/**
 * Local draft of some community fields with dirty tracking. Resets whenever the
 * server copy changes (e.g. another moderator saved).
 */
export function useDraft<T extends Record<string, unknown>>(source: T) {
  const key = JSON.stringify(source);
  const [draft, setDraft] = useState<T>(source);
  useEffect(() => setDraft(source), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = JSON.stringify(draft) !== key;
  const changed = Object.fromEntries(Object.entries(draft).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(source[k]))) as Partial<T>;
  return {
    draft,
    set: <K extends keyof T>(k: K, v: T[K]) => setDraft((d) => ({ ...d, [k]: v })),
    reset: () => setDraft(source),
    dirty,
    changed,
  };
}

export interface SectionProps {
  structure: CommunityStructure;
  /** Navigate within community settings, e.g. go("channels", channelId). */
  go: (section: string, sub?: string) => void;
  sub?: string;
}
